import { z } from 'zod';
import { ACTIVITY_TYPES } from '../activity-types.js';
import { BankAccountStatutoryTypeSchema, LedgerAccountSystemRoleSchema, LedgerKindSchema, LedgerOpeningDateSchema, ProvisionedAccountLedgerAccountSchema } from '../ledger.js';
import { VatBasisSchema, VatFilingCategorySchema } from '../vat.js';
import { TimeIncrementChoiceSchema } from '../time-increment.js';
import { SA_BRANCH_CODE_REGEX, BankAccountTypeSchema } from '../bank.js';
import { SubscriptionStatusSchema } from './billing.js';
import { InvoicePaymentStatusSchema, InvoiceStatusSchema, LiveProformaSchema } from './invoice.js';
import { LowercaseUuidSchema, MAX_MINOR_UNITS, CalendarDateOnlySchema, ClientProjectStatusSchema, PaginationQuerySchema, UnitTypeSchema, createPaginatedResponseSchema, incomeTaxReferenceSchema, payeReferenceSchema, saVatNumberSchema, writeWarningsFields } from './common.js';
import { heldName } from '../entity-document-name.js';
import { nullableText } from '../sanitised-text.js';
// =============================================================================
// Account
// =============================================================================
export const AccountRoleSchema = z.enum(['owner', 'assistant', 'member', 'bookkeeper', 'ai-assistant', 'super-admin']);
// The subset of AccountRoleSchema reachable through human-facing membership
// mutations (invite, self-registration via invite, role change). The bot and
// staff roles are excluded; only guarded super-admin provisioning accepts them.
export const HumanAccountRoleSchema = AccountRoleSchema.exclude(['ai-assistant', 'super-admin']);
/**
 * Whether a role carries assistant-equivalent access (inbox/calendar visibility
 * across the account, activity capture on others' behalf, and so on). True for
 * both the human `assistant` role and bot `ai-assistant` role -
 * the single site every assistant-privilege gate should route through so the
 * two spellings cannot drift apart on a literal string comparison. The
 * member-management carve-out (add/remove/re-role/invite) is a narrower gate
 * than this and stays literal - see `requireOwnerOrAssistantRole`.
 * Returns `false` for `null`/`undefined` (no role resolved).
 */
export function hasAssistantPrivileges(role) {
    if (!role)
        return false;
    return role === 'assistant' || role === 'ai-assistant';
}
export function hasOwnerPrivileges(role) {
    return role === 'owner' || role === 'super-admin';
}
/**
 * Whether a role can perform elevated bookkeeping work: managing firm bank
 * accounts, bank statements, Xero connection settings, and supplier bank
 * details. Deliberately excludes plain members while preserving every role
 * that passed the old owner-or-assistant gate, including the ai-assistant bot.
 */
export function isBookkeepingWriterRole(role) {
    if (!role)
        return false;
    return hasOwnerPrivileges(role) || role === 'assistant' || role === 'ai-assistant' || role === 'bookkeeper';
}
/**
 * Whether a role may change firm-wide fee income settings, such as a fee
 * earner's default fee income account: owner privileges or the bookkeeper.
 * Narrower than `isBookkeepingWriterRole`, which also admits assistants and
 * the ai-assistant bot.
 */
export function isOwnerOrBookkeeperRole(role) {
    return hasOwnerPrivileges(role) || role === 'bookkeeper';
}
/**
 * Whether a membership occupies a client plan seat. Both the bot and a
 * super-admin staff attachment remain visible in the Team table without
 * counting toward the client's plan limit.
 */
export function isHumanMemberRole(role) {
    return role !== 'ai-assistant' && role !== 'super-admin';
}
/**
 * Whether a role should be offered as a fee earner for time and invoice-line
 * attribution. This is narrower than "human member": a bookkeeper consumes a
 * seat but does not bill time to client projects.
 */
export function isFeeEarnerRole(role) {
    return role === 'owner' || role === 'assistant' || role === 'member';
}
/**
 * Whether a member captures time for someone else rather than billing it
 * themselves, so an entry they log without naming a performer belongs to the
 * account's earliest owner. True for assistant-equivalent access and for any
 * role that is not a fee earner. The two limbs overlap on purpose: an
 * `assistant` is a fee earner (they can be named explicitly) but still captures
 * for the owner, while a bookkeeper is not a fee earner at all.
 *
 * Returns `false` when no role resolves (a system path with no membership):
 * capture then stays with the capturer and never goes looking for an owner.
 */
export function capturesOnBehalfOfAnother(role) {
    if (!role)
        return false;
    return hasAssistantPrivileges(role) || !isFeeEarnerRole(role);
}
export const EntityTypeSchema = z.enum(['INDIVIDUAL', 'PTY_LTD', 'INC', 'CC', 'PARTNERSHIP', 'TRUST', 'FOREIGN_COMPANY', 'BODY_CORPORATE', 'NPC', 'VOLUNTARY_ASSOCIATION', 'DECEASED_ESTATE']);
/**
 * Narrow a free-form string into `EntityType` at a trust boundary (a CSV cell,
 * a CLI flag, an untyped request body). Shape follows `isUnitType`
 * (`activity-types.ts`).
 */
export function isEntityType(value) {
    return EntityTypeSchema.options.includes(value);
}
/**
 * The entity types a firm's own account may carry. A deceased estate, a
 * trust, and a voluntary association are all things a firm administers for
 * someone else, never things a firm IS, so the picker on the firm's own
 * business-entity settings is a positive list rather than an exclusion: a
 * future value is withheld from the firm until someone adds it here, which is
 * the safe direction.
 */
export const FIRM_ENTITY_TYPES = ['INDIVIDUAL', 'PTY_LTD', 'INC', 'CC', 'PARTNERSHIP', 'FOREIGN_COMPANY', 'BODY_CORPORATE', 'NPC'];
/**
 * The entity types Bill keeps insolvency books for. An INSOLVENCY administered
 * entity's chart of accounts is derived from the legal type of the entity being
 * wound up - a company or close corporation is liquidated by a liquidator, and
 * an individual, a trust or a partnership is sequestrated by a trustee - and a
 * type Bill has no chart for is refused when the books are provisioned. This is
 * the set that is accepted, published so a picker can withhold the choice
 * rather than offer one that is certain to fail on submit.
 *
 * Each refusal, and the legal reasoning behind it, stays with the map that
 * makes it (`INSOLVENCY_PRESET_BY_ENTITY_TYPE` in the backend ledger domain);
 * a test there asserts that map's supported keys are exactly this set, so the
 * two cannot drift.
 */
export const INSOLVENCY_BOOKS_ENTITY_TYPES = ['INDIVIDUAL', 'PTY_LTD', 'INC', 'CC', 'NPC', 'TRUST', 'PARTNERSHIP'];
/** Whether Bill keeps insolvency books for an entity of this legal type. */
export function supportsInsolvencyBooks(entityType) {
    return INSOLVENCY_BOOKS_ENTITY_TYPES.includes(entityType);
}
/**
 * The entity types Bill keeps business rescue books for. A BUSINESS_RESCUE
 * administered entity's chart of accounts is the same one preset
 * (`sa-business-rescue`) regardless of which admissible type the entity is,
 * but Chapter 6 of the Companies Act 71 of 2008 applies only to a company
 * (PTY_LTD, INC, NPC) and, through item 6 of Schedule 3, a close corporation -
 * a trust or a sole proprietor cannot be placed in business rescue. So the
 * entity type is read as a PRECONDITION on the kind here, not as a selector
 * between charts the way `INSOLVENCY_BOOKS_ENTITY_TYPES` is: a type outside
 * this set is refused when the books are provisioned. This is the set that is
 * accepted, published so a picker can withhold the choice rather than offer
 * one that is certain to fail on submit.
 *
 * The refusal, and the legal reasoning behind it, stays with the resolver
 * that makes it (`resolveEntityPresetId`'s `BUSINESS_RESCUE` case in the
 * backend account domain); a test there asserts the resolver's supported
 * types are exactly this set, so the two cannot drift.
 */
export const BUSINESS_RESCUE_ENTITY_TYPES = ['PTY_LTD', 'INC', 'CC', 'NPC'];
/** Whether Bill keeps business rescue books for an entity of this legal type. */
export function supportsBusinessRescue(entityType) {
    return BUSINESS_RESCUE_ENTITY_TYPES.includes(entityType);
}
/**
 * The entity types whose financial year end defaults to February rather than
 * being chosen. An individual's year of assessment ends on the last day of
 * February (Income Tax Act 58 of 1962 s 1 "year of assessment" read with
 * s 66(13)(a)), and a deceased estate and a trust are assessed on the same
 * cycle. Books for these three types therefore DERIVE their year end from the
 * legal type instead of asking an operator to pick one, which is why
 * `ProvisionAdministeredEntityInputSchema.financialYearEndMonth` is optional
 * for them.
 *
 * `PARTNERSHIP` is left out: its year end is not derivable from the entity
 * type alone while the question whether an insolvent partnership estate is an
 * ITA s 1 "person" stays open (recorded UNVERIFIED in
 * `chart-of-accounts.presets.insolvency.ts` in the backend ledger domain), so
 * an operator chooses it.
 */
export const FEBRUARY_YEAR_END_ENTITY_TYPES = ['INDIVIDUAL', 'DECEASED_ESTATE', 'TRUST'];
/** Whether this legal type's financial year end defaults to February. */
export function isFebruaryYearEndEntityType(entityType) {
    return FEBRUARY_YEAR_END_ENTITY_TYPES.includes(entityType);
}
/**
 * The month these entity types' year of assessment ends in. February, and not
 * configurable: it is the statutory year end, not a default anyone may change.
 */
export const FEBRUARY_FINANCIAL_YEAR_END_MONTH = 2;
/**
 * What kind of books an account keeps. One enum; each value declares which
 * account kinds may carry it in `PRACTICE_TYPE_CARRIERS`.
 *
 * A firm's own practice is ATTORNEY, ADVOCATE, or a non-legal BUSINESS. Set at
 * onboarding from the professional type and changeable in settings.
 *
 * An administered entity's books are kept by a firm on behalf of a legal entity
 * it administers. The entity kinds - entity-only and provisionable - are
 * BUSINESS_RESCUE (a company trading on under a business rescue practitioner),
 * INSOLVENCY (a company or close corporation being wound up), and
 * ESTATE_ADMINISTRATION (a solvent deceased estate the firm administers as
 * executor). Those kinds are chosen once, when the firm sets the entity's
 * books up, and fixed thereafter because they decided the chart of accounts
 * the ledger was seeded with. Entity-admissible types are those kinds plus
 * BUSINESS, which an entity may also carry.
 *
 * Everything that varies by practice type (`@packages/practice-config`, the copy
 * dialect, the chat glossary, the chart-of-accounts preset) is keyed on the
 * whole enum, so adding a value makes the compiler list every surface that has
 * to say what it does for it. Which account kinds may carry a value is declared
 * only in `PRACTICE_TYPE_CARRIERS`. `administeredByAccountId` on the account is
 * what makes an entity-admissible value valid there.
 */
export const PracticeTypeSchema = z.enum([
    'ATTORNEY',
    'ADVOCATE',
    'BUSINESS',
    'BUSINESS_RESCUE',
    'INSOLVENCY',
    'ESTATE_ADMINISTRATION',
]);
/**
 * Where each `PracticeType` value declares which account kinds may carry it.
 * A missing row fails to compile (`satisfies Record<PracticeType, ...>`); a
 * row naming no carrier fails to compile (non-empty tuple). A value may list
 * both carriers. Entity kinds (entity-only, provisionable) are the values
 * listed only for `ADMINISTERED_ENTITY`; entity-admissible types are every
 * value that lists that carrier.
 */
export const PRACTICE_TYPE_CARRIERS = {
    ATTORNEY: ['FIRM'],
    ADVOCATE: ['FIRM'],
    BUSINESS: ['FIRM', 'ADMINISTERED_ENTITY'],
    BUSINESS_RESCUE: ['ADMINISTERED_ENTITY'],
    INSOLVENCY: ['ADMINISTERED_ENTITY'],
    ESTATE_ADMINISTRATION: ['ADMINISTERED_ENTITY'],
};
/**
 * Whether this practice type may sit on this kind of account, per
 * `PRACTICE_TYPE_CARRIERS`. A value with no table row returns false.
 */
export function isPracticeTypeAdmissibleOn(practiceType, carrier) {
    const carriersByPracticeType = PRACTICE_TYPE_CARRIERS;
    const row = carriersByPracticeType[practiceType];
    return row !== undefined && row.some((declared) => declared === carrier);
}
/**
 * `z.enum` needs a non-empty tuple. A filtered list that came out empty is a
 * broken `PRACTICE_TYPE_CARRIERS` table (every value lists at least one
 * carrier, so each derived set must have a member), so fail loud rather than
 * widening the type with a cast.
 */
function requireNonEmptyTuple(values, emptyMessage) {
    const [first, ...rest] = values;
    if (first === undefined) {
        throw new Error(emptyMessage);
    }
    return [first, ...rest];
}
export const FIRM_PRACTICE_TYPES = requireNonEmptyTuple(PracticeTypeSchema.options.filter((practiceType) => isPracticeTypeAdmissibleOn(practiceType, 'FIRM')), 'Invariant violation: PRACTICE_TYPE_CARRIERS admits no practice type on a firm');
export const EntityAdmissiblePracticeTypeSchema = z.enum(requireNonEmptyTuple(PracticeTypeSchema.options.filter((practiceType) => isPracticeTypeAdmissibleOn(practiceType, 'ADMINISTERED_ENTITY')), 'Invariant violation: PRACTICE_TYPE_CARRIERS admits no practice type on an administered entity'));
export const ADMINISTERED_ENTITY_PRACTICE_TYPES = requireNonEmptyTuple(PracticeTypeSchema.options.filter((practiceType) => isPracticeTypeAdmissibleOn(practiceType, 'ADMINISTERED_ENTITY')
    && !isPracticeTypeAdmissibleOn(practiceType, 'FIRM')), 'Invariant violation: PRACTICE_TYPE_CARRIERS admits no entity-only practice type');
export const AdministeredEntityPracticeTypeSchema = z.enum(ADMINISTERED_ENTITY_PRACTICE_TYPES);
export function isAdministeredEntityPracticeType(practiceType) {
    return practiceType !== null && practiceType !== undefined
        && isPracticeTypeAdmissibleOn(practiceType, 'ADMINISTERED_ENTITY')
        && !isPracticeTypeAdmissibleOn(practiceType, 'FIRM');
}
export const InvoiceDueDateModeSchema = z.enum(['DAYS_FROM_INVOICE_DATE', 'END_OF_MONTH_PLUS_DAYS']);
// TIME_BASED: emails bill as defaultEmailTimeUnits hours at the resolved hourly rate (cascade).
// FIXED_RATE: emails bill as a flat fee (defaultEmailRateMinorUnits), ignoring time and the cascade.
export const EmailBillingModeSchema = z.enum(['TIME_BASED', 'FIXED_RATE']);
/**
 * What the onboarding wizard asks a new firm to pick. LAW_FIRM, ATTORNEY and
 * ADVOCATE are the legal practices Bill is built for; BUSINESS is the quiet
 * "not a legal practice" route, which sets the BUSINESS practice type and its
 * copy dialect.
 */
export const ProfessionalTypeSchema = z.enum(['LAW_FIRM', 'ATTORNEY', 'ADVOCATE', 'BUSINESS']);
/**
 * The wire shape shared by every bank account the API returns, whoever owns it:
 * the firm's own accounts, a supplier's banking details, and the business
 * accounts offered as client-statement payment funding. Only fields true of all
 * three belong here - anything an owner-specific route alone can answer goes on
 * a narrower schema that extends this one, as `AccountOwnedBankAccountSchema`
 * below does, rather than becoming a field the other producers can only ever
 * send as a permanent null.
 */
export const BankAccountSchema = z.object({
    id: z.string(),
    businessEntityId: z.string(),
    label: z.string(),
    bankName: z.string().nullable(),
    bankBranchCode: z.string().nullable(),
    bankAccountNumber: z.string().nullable(),
    bankAccountHolderName: z.string().nullable(),
    /**
     * Our record of the account type (CHEQUE, CURRENT, SAVINGS, TRANSMISSION, or
     * OTHER) - see the column comment in bank-account.prisma. Null wherever it
     * was never captured, which is every account created before this field
     * shipped.
     */
    bankAccountType: BankAccountTypeSchema.nullable(),
    currency: z.string(),
    isDefault: z.boolean(),
    /**
     * The account's declared statutory character - the single discriminator,
     * and the only field that tells an s 86(2) pooled trust account apart from
     * an s 86(3) practice investment account and an s 86(4) client investment
     * account.
     *
     * The business-vs-trust question is derived from it, never carried
     * alongside: call `ledgerKindForStatutoryType` for which ledger the
     * account's books live on, `bankRoleForStatutoryType` for which bank role
     * its GL account carries.
     */
    statutoryType: BankAccountStatutoryTypeSchema,
    /**
     * The id of the member who recorded this account - an opaque internal id,
     * not PII. Null on every FIRM-owned account (ownership there is answered by
     * account role, not authorship) and on a SUPPLIER account created before
     * this field shipped (a legacy population, not "created by nobody" - see
     * the column comment in bank-account.prisma). The frontend uses this to
     * decide whether to render the Edit/Archive actions for the current member
     * without provoking a 403: show them when `createdByUserId` matches the
     * viewer, or the viewer is an owner/assistant.
     */
    createdByUserId: z.string().nullable(),
    archivedAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * A bank account owned by the firm itself (`ownerKind: 'ACCOUNT'` on the model)
 * rather than by a supplier, as returned by the firm's own bank-account routes.
 *
 * It is the only response shape that carries `trustInvestmentClientId`, because
 * it is the only one that can: a section 86(4) trust investment account is
 * always the firm's own, and `BankAccountSchema` is also the wire shape for a
 * supplier's banking details and for the business-funding options on a client
 * statement, neither of which can ever be one. Keeping the field off the shared
 * shape is what makes the invariant below true of every response that declares
 * it, instead of a claim contradicted by the majority of its producers.
 */
export const AccountOwnedBankAccountSchema = BankAccountSchema.extend({
    /**
     * The client an s 86(4) investment account is held for, or null on every
     * other account. Non-null exactly when `statutoryType === 'S86_4'`: such an
     * account is opened on one client's instruction and always names that
     * client, so a null here on an S86_4 account would be a broken invariant
     * rather than "not captured".
     */
    trustInvestmentClientId: z.string().nullable(),
});
/**
 * The statutory character a bank account is created with, and the client it is
 * held for when that client is part of what the account IS.
 *
 * A discriminated union rather than a `statutoryType` field beside an optional
 * `clientId`, so that "an s 86(4) account names exactly one client" is a
 * property of the shape: exactly one of the three members is a well-formed
 * body, and the two runtime checks that used to enforce the pairing have
 * nothing left to reject - though for different reasons, which the note below
 * spells out. The same reasoning, and the same shape, as `FundingSourceSchema`
 * in document-payment.ts.
 *
 * Either s 86 value additionally requires the account to already have an
 * active TRUST ledger (POST /seed-chart-of-accounts with the trust preset)
 * and a practice type that does trust accounting. That one stays a runtime
 * check: it is a fact about the ACCOUNT's current state, not about the shape
 * of this request, so no schema can decide it.
 *
 * ONE HALF OF THE PAIRING IS STRUCTURAL, THE OTHER IS MERELY HARD TO GET
 * WRONG, and the difference is worth knowing before trusting this shape too
 * far. An S86_4 body with no client does not parse - `clientId` is required on
 * that member and no other member matches, so it is a 400 from validation.
 * A `clientId` smuggled into a BUSINESS or S86_2 member is NOT rejected: these
 * are ordinary (non-strict) objects, so Zod strips the unknown key and the
 * account is opened without it. That was a real risk while `statutoryType` and
 * `clientId` were adjacent optional fields on a flat body - setting one and
 * forgetting the other was a plausible slip, and a runtime check rejected it.
 * It is a much smaller risk here: there is no top-level `clientId` to set any
 * more, so the same mistake now means nesting a key inside a member that does
 * not declare one. Still deliberately not `strictObject`, but for a narrower
 * reason than it used to be: the AJV `removeAdditional: true` setting that
 * once made `.strict()` on a `oneOf` member unreliable (it could strip
 * against the wrong branch before the right one matched) is gone - AJV now
 * rejects an unrecognised key instead of deleting it, so `strictObject` would
 * reach the wire correctly here too. It stays unstrict because nobody has
 * checked whether making one member strict, while the runtime check above
 * still guards all three, is worth the schema change - not because AJV would
 * undermine it.
 *
 * It lives under a `statutory` envelope key on the create body because a
 * discriminated union cannot sit beside sibling top-level fields - see the
 * note on `RecordInvoicePaymentBodySchema`'s `funding` key.
 */
export const CreateBankAccountStatutorySchema = z.discriminatedUnion('type', [
    /** The firm's own operating account, or a supplier's. */
    z.object({ type: z.literal('BUSINESS') }),
    /**
     * The pooled trust banking account required by s 86(2) of the Legal
     * Practice Act, holding every client's trust money together. Named for no
     * single client, so it carries none.
     */
    z.object({ type: z.literal('S86_2') }),
    /**
     * A practice trust investment account under s 86(3), opened for surplus
     * trust money and held for no identified client.
     */
    z.object({ type: z.literal('S86_3') }),
    /**
     * A trust investment account opened on ONE client's instruction under
     * s 86(4). The client is not an attribute of the account so much as part of
     * what it is - pooling clients in one is not permissible - so it is
     * required here rather than optional. Must name an active client of this
     * account; ownership is verified at the route.
     */
    z.object({ type: z.literal('S86_4'), clientId: z.string().uuid() }),
]);
export const BANK_ACCOUNT_NUMBER_MIN_LENGTH = 6;
export const BANK_ACCOUNT_NUMBER_MAX_LENGTH = 16;
export const CreateBankAccountInputSchema = z.object({
    label: z.string().min(1, 'Label is required').max(100, 'Label must be 100 characters or less'),
    bankName: nullableText(100),
    bankBranchCode: z.string().regex(SA_BRANCH_CODE_REGEX, 'Branch code must be exactly 6 digits'),
    bankAccountNumber: z.string()
        .min(BANK_ACCOUNT_NUMBER_MIN_LENGTH, `Account number must be at least ${BANK_ACCOUNT_NUMBER_MIN_LENGTH} digits`)
        .max(BANK_ACCOUNT_NUMBER_MAX_LENGTH, `Account number must be at most ${BANK_ACCOUNT_NUMBER_MAX_LENGTH} digits`),
    bankAccountHolderName: nullableText(200),
    bankAccountType: BankAccountTypeSchema.nullable().optional(),
    isDefault: z.boolean().optional(),
    /**
     * What the account is, and - for an s 86(4) investment account - whose
     * money it holds. See `CreateBankAccountStatutorySchema`.
     *
     * Required, with no default. It was optional while a deprecated `kind`
     * alias could supply the same answer, and an omitted value then meant
     * BUSINESS; making it explicit is what lets the pairing with `clientId` be
     * structural rather than a runtime check (you cannot discriminate on a
     * field that may be absent).
     */
    statutory: CreateBankAccountStatutorySchema,
});
export const UpdateBankAccountInputSchema = z.object({
    label: z.string().min(1, 'Label is required').max(100).optional(),
    bankName: nullableText(100),
    bankBranchCode: z.string().regex(SA_BRANCH_CODE_REGEX, 'Branch code must be exactly 6 digits').nullable().optional(),
    bankAccountNumber: z.string().min(BANK_ACCOUNT_NUMBER_MIN_LENGTH).max(BANK_ACCOUNT_NUMBER_MAX_LENGTH).nullable().optional(),
    bankAccountHolderName: nullableText(200),
    bankAccountType: BankAccountTypeSchema.nullable().optional(),
    isDefault: z.boolean().optional(),
});
export const BankAccountListResponseSchema = createPaginatedResponseSchema(AccountOwnedBankAccountSchema);
/**
 * Optional identity and tax fields shared by the account, client, and supplier
 * write schemas so `vatBasis` and `registrationDate` cannot drift between them.
 * Omitted keys mean leave unchanged; `null` clears a nullable field.
 */
export const businessEntityIdentityTaxInput = {
    tradingName: nullableText(300),
    formerNames: z.array(z.string()
        .transform((value) => value.trim())
        .pipe(z.string().min(1).max(300))).max(20).optional(),
    registrationDate: CalendarDateOnlySchema.nullable().optional(),
    industry: nullableText(100),
    description: nullableText(500),
    incomeTaxReference: incomeTaxReferenceSchema,
    payeReference: payeReferenceSchema,
    vatBasis: VatBasisSchema.nullable().optional(),
};
export const BusinessEntitySchema = z.object({
    id: z.string(),
    legalName: z.string().nullable(),
    entityType: EntityTypeSchema.nullable(),
    registrationNumber: z.string().nullable(),
    vatNumber: z.string().nullable(),
    vatRegisteredFrom: CalendarDateOnlySchema.nullable(),
    tradingName: z.string().nullable(),
    formerNames: z.array(z.string()),
    registrationDate: CalendarDateOnlySchema.nullable(),
    industry: z.string().nullable(),
    description: z.string().nullable(),
    incomeTaxReference: z.string().nullable(),
    payeReference: z.string().nullable(),
    vatBasis: VatBasisSchema.nullable(),
    financialYearEndMonth: z.number().int().min(1).max(12).nullable(),
    vatFilingCategory: VatFilingCategorySchema.nullable(),
    addressLine1: z.string().nullable(),
    addressLine2: z.string().nullable(),
    addressCity: z.string().nullable(),
    addressProvince: z.string().nullable(),
    addressPostalCode: z.string().nullable(),
    addressCountry: z.string(),
    bankAccounts: z.array(BankAccountSchema).optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const PracticeSettingsResponseSchema = z.object({
    defaultEmailTimeUnits: z.number(),
    // Email pricing mode and the flat fee it uses. defaultEmailRateMinorUnits is null
    // unless emailBillingMode is FIXED_RATE.
    emailBillingMode: EmailBillingModeSchema,
    defaultEmailRateMinorUnits: z.int().nullable(),
    defaultPerKmRateMinorUnits: z.int(),
    defaultPerPageRateMinorUnits: z.int(),
    defaultCurrency: z.string(),
    practiceType: PracticeTypeSchema.nullable(),
    // Raw stored value in hours. Kept for back-compat and for direct use by the
    // input-time steering helpers in time-increment.ts (applyTimeIncrement,
    // isHoursMultipleOfIncrement), which take hours - never consulted at read time.
    timeIncrement: z.number().nullable(),
    // Enum projection of `timeIncrement`. UI reads this to avoid ever rendering the raw float
    // (e.g. `0.0833...` for 5 min).
    timeIncrementChoice: TimeIncrementChoiceSchema.nullable(),
    defaultInvoiceDueDateMode: InvoiceDueDateModeSchema.nullable(),
    defaultInvoiceDueDateDays: z.int().min(0).max(366).nullable(),
});
export const UpdatePracticeSettingsInputSchema = z.object({
    defaultEmailTimeUnits: z.number().min(0).max(24).optional(),
    emailBillingMode: EmailBillingModeSchema.optional(),
    // Flat fee per email in minor units. Read only when emailBillingMode = FIXED_RATE;
    // the service enforces "set and > 0" once merged with the stored row.
    defaultEmailRateMinorUnits: z.int().min(0).nullable().optional(),
    // Int4 ceiling guards against DB overflow becoming a 500. R21M per km/page
    // is far above any plausible rate; the cap exists to surface bad input as a
    // clean 400 rather than a database error.
    defaultPerKmRateMinorUnits: z.int().min(0).optional(),
    defaultPerPageRateMinorUnits: z.int().min(0).optional(),
    defaultCurrency: z.string().regex(/^[A-Z]{3}$/, 'Currency must be a 3-letter ISO 4217 code (e.g. ZAR)').optional(),
    practiceType: PracticeTypeSchema.optional(),
    // Named enum avoids sending raw floats over the wire. Service converts to hours for
    // storage via `hoursFromChoice`.
    timeIncrement: TimeIncrementChoiceSchema.nullable().optional(),
    defaultInvoiceDueDateMode: InvoiceDueDateModeSchema.nullable().optional(),
    defaultInvoiceDueDateDays: z.int().min(0).max(366).nullable().optional(),
}).superRefine((data, ctx) => {
    // FIXED_RATE email billing requires a positive flat rate. The wire payload can only be
    // judged when the rate is present in the same patch; a patch that sets the mode alone
    // (rate already stored) is validated authoritatively in the service layer after merging.
    if (data.emailBillingMode === 'FIXED_RATE' && data.defaultEmailRateMinorUnits !== undefined) {
        if (data.defaultEmailRateMinorUnits === null || data.defaultEmailRateMinorUnits <= 0) {
            ctx.addIssue({
                code: 'custom',
                path: ['defaultEmailRateMinorUnits'],
                message: 'A fixed email rate greater than zero is required when email billing is set to a fixed rate.',
            });
        }
    }
    const modePresent = data.defaultInvoiceDueDateMode !== undefined && data.defaultInvoiceDueDateMode !== null;
    const daysPresent = data.defaultInvoiceDueDateDays !== undefined && data.defaultInvoiceDueDateDays !== null;
    const modeExplicitNull = data.defaultInvoiceDueDateMode === null;
    const daysExplicitNull = data.defaultInvoiceDueDateDays === null;
    // Both set: valid.
    if (modePresent && daysPresent)
        return;
    // Both absent (neither key supplied): valid (no change to either field).
    if (data.defaultInvoiceDueDateMode === undefined && data.defaultInvoiceDueDateDays === undefined)
        return;
    // Both explicitly null (clear both): valid.
    if (modeExplicitNull && daysExplicitNull)
        return;
    // Any other combination has exactly one of the pair non-null in the wire payload.
    ctx.addIssue({
        code: "custom",
        path: ['defaultInvoiceDueDateDays'],
        message: 'defaultInvoiceDueDateMode and defaultInvoiceDueDateDays must be set or cleared together',
    });
});
/**
 * Allowed character set for the client-project reference prefix:
 * alphanumerics plus `-`, `.`, `(`, `)`, `_`. Length 1-10. URL-significant
 * bytes (`/`, `#`, `&`, etc.) are deliberately excluded so the rendered
 * reference is safe to embed in URL paths and query strings without escaping.
 *
 * Single source of truth - re-derive from this constant in both backend
 * validation and any frontend Zod schemas (mirrors the `SA_VAT_REGEX` pattern
 * above). The user-facing label in the Bill UI continues to read "matter".
 */
export const CLIENT_PROJECT_REFERENCE_PREFIX_REGEX = /^[A-Za-z0-9\-.()_]{1,10}$/;
export const CLIENT_PROJECT_REFERENCE_PREFIX_DESCRIPTION = 'Must be 1-10 characters from A-Z, a-z, 0-9, and -.()_';
export const ClientProjectReferenceSettingsResponseSchema = z.object({
    clientProjectReferencePrefix: z.string().regex(CLIENT_PROJECT_REFERENCE_PREFIX_REGEX),
    // Always renders as `{prefix}{NNNN}` (literal concatenation, no separator).
    // Auto-generation is always on; the prefix is required.
    nextClientProjectReference: z.string(),
});
export const UpdateClientProjectReferenceSettingsInputSchema = z.object({
    clientProjectReferencePrefix: z
        .string()
        .regex(CLIENT_PROJECT_REFERENCE_PREFIX_REGEX, { error: CLIENT_PROJECT_REFERENCE_PREFIX_DESCRIPTION }),
});
export const ResolveRateQuerySchema = z.object({
    activityType: z.enum(ACTIVITY_TYPES).extract(['EMAIL']).optional().describe('EMAIL for an email entry: on a FIXED_RATE account it resolves the flat per-email rate; on a TIME_BASED account it changes nothing. Omit for other entries.'),
    unitType: UnitTypeSchema.describe("pages -> the per-page rate, km -> the per-km rate, hours uses the performer’s own matter rate, then their own client rate, then shared matter > shared client > member; other units use shared matter > shared client > member. With activityType=EMAIL on a FIXED_RATE account only lump_sum (create with units=1) or items is accepted, since the flat email rate is a per-email fee, not hourly; others are rejected (400)."),
    clientProjectId: z.string().describe('Matter/project ID to resolve the rate for'),
    performedByUserId: z.string().optional().describe("Whoever did the work; pass it whenever that is not the caller. For a FIXED_RATE email it picks that member's own per-email rate over the account default (omitted, the default applies even when they have one); for hours it selects their own matter/client overrides before shared defaults, then their Team hourly rate as the final fallback."),
});
export const RateSourceSchema = z.enum(['fee-earner-client-project', 'fee-earner-client', 'client-project', 'client', 'member', 'account', 'none']);
export const ResolveRateResponseSchema = z.object({
    ratePerUnitMinorUnits: z.int(),
    source: RateSourceSchema,
});
// =============================================================================
// Administered entities
//
// A firm that runs the books of a legal entity it administers - a business
// rescue, an insolvency, a deceased estate - gets a separate Account for that
// entity, reached from the firm's client record. User-facing copy calls it an
// "entity" and its books "<legal name>'s books"; never a "client ledger", which
// in South African practice means trust money under LPC Rule 54.
// =============================================================================
/**
 * The firm side of an administered entity, as seen from inside the entity's own
 * account. Non-null exactly when the current account IS an administered entity,
 * which is what the app shell reads to show the "administered by <firm>" bar and
 * the way back to the client page.
 */
export const AdministeredBySchema = z.object({
    accountId: z.string(),
    accountName: z.string(),
    /** The firm-side client whose legal entity these books belong to. */
    clientId: z.string(),
});
/**
 * What the firm supplies when it sets an entity's books up. The kind decides the
 * chart of accounts the ledger is seeded with, so it is required and fixed from
 * here on; the rest is the same ledger setup a firm's own books go through
 * (`CreateLedgerInputSchema`), because an entity's ledger is a ledger like any
 * other: books open on the 1st of a month, a year end, and the entity's VAT
 * position. The VAT number is written to the business entity the firm's client
 * record shares with the entity account.
 *
 * `financialYearEndMonth` is OPTIONAL because for some entities it is not a
 * choice. An individual, a deceased estate and a trust are assessed on a year
 * ending in February (`FEBRUARY_YEAR_END_ENTITY_TYPES`), so provisioning
 * derives the month from the client's legal type and a caller does not supply
 * one. Every other type keeps a real year end, and omitting it there is
 * refused.
 *
 * The rule is keyed on the ENTITY TYPE rather than the kind, because that is
 * where it actually comes from: a sequestrated individual or trust reaches it
 * through INSOLVENCY and a deceased estate through ESTATE_ADMINISTRATION, but
 * in each case it is the entity type behind the books that fixes the year end.
 * The refusal, and the derivation, live in `provisionAdministeredEntity`,
 * which is the layer that holds the client's entity type - this schema cannot
 * see it, so it does not try to enforce the pairing here.
 */
export const ProvisionAdministeredEntityInputSchema = z.object({
    practiceType: AdministeredEntityPracticeTypeSchema,
    openingDate: LedgerOpeningDateSchema,
    financialYearEndMonth: z.number().int().min(1).max(12).optional(),
    vatFilingCategory: VatFilingCategorySchema.nullable().optional(),
    vatNumber: saVatNumberSchema,
});
/** One row of the firm-side "entities we administer" list. */
export const AdministeredEntitySchema = z.object({
    accountId: z.string(),
    name: z.string(),
    clientId: z.string(),
    /**
     * The stored type admissible on an entity (the entity kinds plus BUSINESS),
     * so the firm's client page can say "in business rescue" without a second
     * fetch. Null on a legacy entity provisioned before kinds existed; the seed
     * and every provisioning path since write one.
     */
    practiceType: EntityAdmissiblePracticeTypeSchema.nullable(),
});
export const AdministeredEntityListResponseSchema = z.object({
    data: z.array(AdministeredEntitySchema),
});
export const AccountSchema = z.object({
    id: z.string(),
    name: z.string(),
    logoUrl: z.string().nullable().optional(),
    businessEntity: BusinessEntitySchema.nullable().optional(),
    provisionedAt: z.string().nullable().optional(),
    /**
     * The firm administering this account, when this account holds the books of
     * an entity that firm administers; null on an ordinary firm account.
     *
     * `.optional()` alongside `.nullable()` for the same reason the fields above
     * carry it: only `GET /accounts/:accountId` resolves this, and the other
     * producers of this shape (create, update, logo upload) omit the key
     * entirely. Read an absent key exactly as null - not as "unknown".
     */
    administeredBy: AdministeredBySchema.nullable().optional(),
    /**
     * Whether this account may set up books for an entity it administers - the
     * interim rollout gate, true for a firm with the `entity-books` feature flag
     * on and for a staff caller. The frontend gates the "Set up entity books" affordance on
     * it so the button appears exactly where the provisioning route would
     * accept it, rather than the frontend keeping a second copy of the list.
     *
     * `.optional()` for the same reason as the fields around it: only
     * `GET /accounts/:accountId` resolves it. Read an absent key as `false`,
     * which fails closed - hiding an affordance a caller might have been
     * allowed costs one support question, whereas showing one the server
     * refuses costs a 403 after the user has filled in a dialog.
     */
    entityBooksEnabled: z.boolean().optional(),
    /**
     * Whether the accounting surfaces (ledger, journals, bank accounts,
     * financial reports) are enabled for this account - the `accounting`
     * feature flag as the backend resolves it, true for an account the flag is
     * on for, for an administered entity whose firm has it, and for a staff
     * caller. The frontend gates the accounting navigation and routes on it
     * rather than keeping its own copy of who is on the programme.
     *
     * `.optional()` for the same reason as the fields around it: only
     * `GET /accounts/:accountId` resolves it. Read an absent key as `false`,
     * which fails closed.
     */
    accountingEnabled: z.boolean().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    practiceSettings: PracticeSettingsResponseSchema.extend(ClientProjectReferenceSettingsResponseSchema.shape).optional(),
});
/**
 * Response for `POST .../administered-entity`: the provisioned entity's
 * `Account`, plus the shared `warnings` / `warningDetails` pair a write
 * carrying `vatNumber` can populate (see `vatCheckDigitWarning` in
 * `common.js`).
 */
export const ProvisionAdministeredEntityResponseSchema = AccountSchema.extend(writeWarningsFields);
export const AccountMemberSchema = z.object({
    id: z.string(),
    role: AccountRoleSchema,
    user: z.object({
        id: z.string(),
        name: z.string(),
        email: z.string(),
        image: z.string().nullable(),
    }),
    hourlyRateMinorUnits: z.int().nullable(),
    hourlyRateCurrency: z.string(),
    /**
     * Per-member override of `PracticeSetting.defaultEmailRateMinorUnits`, read
     * only when the account's `emailBillingMode` is `FIXED_RATE`. Raw stored
     * value: `null` means this member inherits the account-level flat email
     * rate; a set value overrides it for activities this member performs.
     *
     * `.optional()` alongside `.nullable()`: this is a client-side response
     * validator, and an old deployed backend (e.g. mid-rollback, before the
     * Firebase-hosted frontend is reverted) omits the key entirely rather
     * than sending null. Treat an absent key the same as null (inherit) at
     * every read site - never assume presence.
     */
    emailRateMinorUnits: z.int().nullable().optional(),
    createdAt: z.string(),
});
export const AccountWithMembersSchema = AccountSchema.extend({
    members: z.array(AccountMemberSchema),
});
export const UserAccountSchema = z.object({
    id: z.string(),
    role: AccountRoleSchema,
    account: AccountSchema,
    createdAt: z.string(),
});
// Request/Response schemas
export const CreateAccountInputSchema = z.object({
    name: z.string().min(1).max(100),
});
export const CreateAccountResponseSchema = z.object({
    account: AccountSchema,
    token: z.string(),
});
export const UpdateAccountInputSchema = z.object({
    name: z.string().min(1, 'Account name is required').max(100, 'Account name must be 100 characters or less'),
});
export const AddMemberInputSchema = z.object({
    userId: z.string(),
    role: HumanAccountRoleSchema.optional(),
});
export const UpdateMemberRoleInputSchema = z.object({
    role: HumanAccountRoleSchema,
});
export const AccountListResponseSchema = createPaginatedResponseSchema(UserAccountSchema);
export const CompleteOnboardingInputSchema = z.object({
    fullName: z.string().min(1).max(100).optional(),
    legalName: z.string().min(1).max(200).optional(),
    tradingName: z.string().trim().min(1).max(200).optional(),
    professionalType: ProfessionalTypeSchema.optional(),
    hourlyRateMinorUnits: z.int().min(0).nullable().optional(),
}).refine(data => data.legalName !== undefined || data.tradingName !== undefined, { path: ['tradingName'], message: 'Name is required' });
export const CompleteOnboardingResponseSchema = z.object({
    account: AccountSchema,
    businessEntity: BusinessEntitySchema,
    welcomeConversationId: z.string().uuid(),
});
export const UpdateBusinessEntityInputSchema = z.object({
    expectedUpdatedAt: z.string().datetime().nullable().optional(),
    // Omit leaves the stored value; blank, whitespace or null means no legal
    // name and is stored as null (the single meaning of "no legal name").
    // heldName rather than nullableText because it also trims a padded name,
    // which nullableText keeps as typed.
    legalName: z.string().max(200).nullable().transform(v => heldName(v)).optional(),
    entityType: EntityTypeSchema.nullable().optional(),
    registrationNumber: nullableText(50),
    vatNumber: saVatNumberSchema,
    ...businessEntityIdentityTaxInput,
    // Firm-only settings. Not in businessEntityIdentityTaxInput: that bag is the
    // client and supplier write path, and nothing reads a client's or supplier's
    // VAT registration date, nor may a client's VAT category overwrite the
    // practice's. Omit leaves the stored value; null clears the person-level
    // setting on the business entity.
    vatRegisteredFrom: CalendarDateOnlySchema.nullable().optional(),
    financialYearEndMonth: z.number().int().min(1).max(12).nullable().optional(),
    vatFilingCategory: VatFilingCategorySchema.nullable().optional(),
    addressLine1: nullableText(),
    addressLine2: nullableText(),
    addressCity: nullableText(),
    addressProvince: nullableText(),
    addressPostalCode: nullableText(),
    addressCountry: z.string().length(2).optional(),
    // TODO: Partial address updates may leave address in inconsistent state -- consider grouped validation in future
});
/**
 * Response for both business-entity PATCH routes (the account owner's own
 * write, and the admin write on an account it does not own): the updated
 * `BusinessEntity`, plus the shared `warnings` / `warningDetails` pair a
 * write carrying `vatNumber` can populate (see `vatCheckDigitWarning` in
 * `common.js`) - never a refusal, per "Record what happened; guide what
 * should happen" in the root CLAUDE.md.
 */
export const UpdateBusinessEntityResponseSchema = BusinessEntitySchema.extend(writeWarningsFields);
export const UpdateHourlyRateInputSchema = z.object({
    hourlyRateMinorUnits: z.int().min(0).nullable(),
    hourlyRateCurrency: z.string().length(3).optional(),
});
/**
 * `emailRateMinorUnits` rejects `0`, unlike `hourlyRateMinorUnits` (which
 * accepts `min(0)`): the email cascade in `resolveRate` is a bare `??`, so a
 * stored `0` would price this member's emails at zero rather than inherit the
 * firm default (the hourly cascade instead SKIPS a stored `0` via its own
 * `> 0` guard, so `0` is harmless there). Pass `null` to clear the override
 * and inherit `PracticeSetting.defaultEmailRateMinorUnits`.
 *
 * The `<= 0` rejection is enforced in `updateEmailRateDirect`
 * (`account-membership.service.ts`), not here: Zod's per-field `error`
 * message is dropped when this schema is compiled to JSON Schema for route
 * validation (`z.toJSONSchema` has no way to carry it), so a message set on
 * this schema never reaches the user. The domain service is the layer that
 * both enforces the invariant and returns a user-facing message. This schema
 * only constrains the wire shape (an integer or null).
 */
export const UpdateEmailRateInputSchema = z.object({
    emailRateMinorUnits: z.int().nullable(),
});
// =============================================================================
// Fee earner default fee income account
//
// Which income account a fee earner's new fee lines land on. The list holds
// current fee earners only and is bounded by the firm's seat count.
// =============================================================================
export const FeeEarnerDefaultFeeAccountParamsSchema = z.object({
    accountId: z.string(),
    userId: LowercaseUuidSchema,
});
export const FeeEarnerDefaultFeeAccountSchema = z.object({
    userId: z.string(),
    name: z.string(),
    role: AccountRoleSchema,
    /**
     * The account this fee earner's new fee lines default to. null means the
     * firm default fee account: nothing is set, or the stored account is
     * archived or no longer an income account on the business ledger.
     */
    defaultFeeLedgerAccountId: z.string().nullable(),
});
export const FeeEarnerDefaultFeeAccountListResponseSchema = z.object({
    data: z.array(FeeEarnerDefaultFeeAccountSchema),
});
/** null clears the mapping, so the fee earner follows the firm default. */
export const SetDefaultFeeLedgerAccountInputSchema = z.strictObject({
    ledgerAccountId: LowercaseUuidSchema.nullable(),
});
// =============================================================================
// Account Invites
// =============================================================================
export const AccountInviteStatusSchema = z.enum(['PENDING', 'ACCEPTED', 'CANCELLED', 'EXPIRED']);
export const AccountInviteSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    email: z.string(),
    status: AccountInviteStatusSchema,
    role: AccountRoleSchema,
    invitedBy: z.string(),
    expiresAt: z.string(),
    createdAt: z.string(),
    lastSentAt: z.string(),
});
export const AccountInviteListResponseSchema = createPaginatedResponseSchema(AccountInviteSchema);
export const CreateInviteInputSchema = z.object({
    email: z.email('Enter a valid email address'),
    role: HumanAccountRoleSchema.default('member'),
});
export const RecipientStatusSchema = z.enum(['new', 'existing']);
export const InviteDetailsSchema = z.object({
    id: z.string(),
    accountName: z.string(),
    inviterName: z.string(),
    email: z.string(),
    role: AccountRoleSchema,
    expiresAt: z.string(),
    recipientStatus: RecipientStatusSchema,
});
export const AcceptInviteInputSchema = z.object({
    token: z.string().min(1).max(512),
});
export const AcceptInviteWithPasswordInputSchema = z.object({
    token: z.string().min(1).max(512),
    password: z.string().min(12, 'Password must be at least 12 characters.').max(128),
    name: z.string().trim().min(1).max(200),
    idempotencyKey: z.string().min(1).max(128),
});
export const AcceptInviteResponseSchema = z.object({
    token: z.string(),
    accountId: z.string(),
    provisioned: z.boolean(),
});
export const ResendInviteResponseSchema = AccountInviteSchema;
// =============================================================================
// Account Provisioning (Super Admin)
// =============================================================================
/**
 * Trial plan keys a super admin may select when provisioning an account.
 * Mirrors `TrialPlanKey` in `apps/backend/platform/src/domains/billing/billing.service.ts`
 * minus `'landingTrial'`, which is reserved for self-serve signup.
 */
export const ProvisionTrialPlanKeySchema = z.enum(['trial', 'trialSmallFirm', 'trialMediumFirm']);
export const ProvisionAccountInputSchema = z.object({
    name: z.string().min(1).max(100),
    businessEntity: z.object({
        legalName: z.string().min(1).max(200),
        entityType: EntityTypeSchema.nullable().optional(),
        vatNumber: saVatNumberSchema,
        addressLine1: nullableText(200),
        addressLine2: nullableText(200),
        addressCity: nullableText(100),
        addressProvince: nullableText(100),
        addressPostalCode: nullableText(20),
        addressCountry: z.string().length(2).optional(),
    }),
    // Defaults to 'trial' (3 members) when omitted. Use 'trialMediumFirm' (10
    // members) for medium law firms evaluating Bill ahead of a bespoke plan.
    trialPlanKey: ProvisionTrialPlanKeySchema.optional(),
});
export const ProvisionAccountResponseSchema = z.object({
    account: AccountSchema,
    businessEntity: BusinessEntitySchema,
    ...writeWarningsFields,
});
export const ProvisionedAccountSummarySchema = z.object({
    accountName: z.string(),
    businessEntityName: z.string().nullable(),
    clientCount: z.number(),
    projectCount: z.number(),
    provisionedAt: z.string().nullable(),
    ownerInvite: z.object({ email: z.string(), status: z.string(), createdAt: z.string() }).nullable(),
    // Every LedgerAccount across every ledger the account owns. Empty array
    // when the account has not yet been seeded with a chart of accounts; the
    // admin detail page uses this to decide whether to show the preset picker
    // or render the existing chart.
    ledgerAccounts: z.array(ProvisionedAccountLedgerAccountSchema),
});
// The financial footprint a deletion would destroy - see
// `accountService.getAccountDeletionGate` / `assertAccountDeletable`. The same
// enum populates two arrays on the preview, and which array a value lands in
// is what decides whether the deletion may proceed:
//
//   - `blockers`   - refuse. An empty array means the account is deletable;
//                    there is no separate boolean, since `blockers.length === 0`
//                    would be a second, driftable source of truth for the same
//                    fact.
//   - `advisories` - destroy knowingly. The operator is told what goes and may
//                    still proceed.
//
// Only an administered entity ever carries advisories, and only for the
// footprint values; see `getAccountDeletionGate` for why.
export const AccountDeletionBlockerSchema = z.enum([
    'POSTED_JOURNAL_ENTRIES',
    'ISSUED_INVOICES',
    'RECORDED_PAYMENTS',
    'IMPORTED_BANK_STATEMENTS',
    'BILLABLE_SUBSCRIPTION',
    'ADMINISTERS_ENTITIES',
]);
export const AccountDeletionPreviewSchema = z.object({
    accountId: z.string(),
    accountName: z.string(),
    memberCount: z.number(),
    clientCount: z.number(),
    projectCount: z.number(),
    activityCount: z.number(),
    hasXeroConnection: z.boolean(),
    blockers: z.array(AccountDeletionBlockerSchema),
    /**
     * Footprint that will be destroyed but does not refuse the deletion. Always
     * empty for a firm; populated only for an administered entity, whose books
     * have no other deletion route (see `getAccountDeletionGate`).
     */
    advisories: z.array(AccountDeletionBlockerSchema),
    warning: z.string(),
});
export const AccountDeletionResponseSchema = z.object({
    success: z.boolean(),
    preview: AccountDeletionPreviewSchema.optional(),
});
// =============================================================================
// Ledger Backfill (Super Admin)
// =============================================================================
/**
 * A single account entry returned by the without-ledger endpoint.
 * Carries only the fields the backfill CLI needs: the account ID (to call
 * provision-minimal and the per-document repair endpoints) and the name
 * (for human-readable progress output).
 */
export const AccountWithoutLedgerSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
});
export const AccountsWithoutLedgerResponseSchema = z.object({
    data: z.array(AccountWithoutLedgerSchema),
});
/**
 * A bank account whose books-representation is incomplete: the account has a
 * ledger of the kind its statutory type puts it on, but the bank account is
 * missing one or more of its three bank-scoped role rows (bank-role, clearing,
 * suspense): BANK + PAYMENTS_CLEARING + BANK_SUSPENSE on a BUSINESS ledger,
 * TRUST_BANK_S86_2 + PAYMENTS_CLEARING + BANK_SUSPENSE on a TRUST ledger - the
 * clearing and suspense roles are the same on both. Returned by the
 * unlinked-bank-accounts discovery endpoint; repaired by the per-account
 * link-bank-accounts endpoint. Banks on accounts with no ledger of that kind
 * are excluded - those are the without-ledger backfill's population.
 */
export const UnlinkedBankAccountSchema = z.object({
    accountId: z.string().uuid(),
    accountName: z.string(),
    bankAccountId: z.string().uuid(),
    bankAccountLabel: z.string(),
    /** The ledger this bank account's books live on - see `ledgerKindForStatutoryType`. */
    ledgerKind: LedgerKindSchema,
    missingRoles: z.array(LedgerAccountSystemRoleSchema),
});
export const UnlinkedBankAccountsResponseSchema = z.object({
    data: z.array(UnlinkedBankAccountSchema),
});
/**
 * Discriminates the three outcomes the link-bank-accounts repair endpoint can
 * report for one bank account:
 * - `linked`: the bank-role GL account is linked after the call (adopted,
 *   created, or already linked before the call - all reported identically).
 *   `ledgerAccountId` carries the linked GL account id.
 * - `deferred`: a ledger of the bank's own kind exists, but more than one
 *   unlinked bank-role candidate makes the automatic choice ambiguous. The bank stays
 *   fully unlinked. Running provision-minimal again cannot fix this - the
 *   operator must resolve it explicitly via
 *   `POST .../ledgers/:ledgerId/accounts/:ledgerAccountId/link-bank-account`
 *   (or the equivalent Bill UI flow), choosing which candidate the bank claims.
 * - `no-ledger`: the account has no ledger of the bank's own kind yet. Run
 *   provision-minimal first, then re-run link-bank-accounts.
 */
export const LinkedBankAccountOutcomeSchema = z.enum(['linked', 'deferred', 'no-ledger']);
/**
 * Per-bank outcome of the link-bank-accounts repair endpoint. `ledgerAccountId`
 * is the bank-role GL account the bank account is linked to after the call
 * (adopted, created, or already linked - all reported identically) when
 * `outcome === 'linked'`, and null for both `deferred` and `no-ledger` -
 * `outcome` is what tells the two null cases apart; see
 * `LinkedBankAccountOutcomeSchema` above.
 */
export const LinkedBankAccountResultSchema = z.object({
    bankAccountId: z.string().uuid(),
    bankAccountLabel: z.string(),
    /** The ledger this bank account's books live on - see `ledgerKindForStatutoryType`. */
    ledgerKind: LedgerKindSchema,
    ledgerAccountId: z.string().uuid().nullable(),
    outcome: LinkedBankAccountOutcomeSchema,
});
export const LinkBankAccountsResponseSchema = z.object({
    data: z.array(LinkedBankAccountResultSchema),
});
export const ProvisionedAccountListQuerySchema = z.object({
    search: z.string().max(200).optional(),
}).extend(PaginationQuerySchema.shape);
export const ProvisionedAccountListItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    provisionedAt: z.string(),
    businessEntity: BusinessEntitySchema.nullable(),
    ownerInvite: z.object({
        email: z.string(),
        status: z.string(),
        createdAt: z.string(),
    }).nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const ProvisionedAccountListResponseSchema = createPaginatedResponseSchema(ProvisionedAccountListItemSchema);
// All accounts list (super admin) - includes self-signup and provisioned accounts
export const AdminAccountListItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    provisionedAt: z.string().nullable(),
    businessEntity: BusinessEntitySchema.nullable(),
    ownerEmail: z.string().nullable(),
    subscription: z.object({
        status: SubscriptionStatusSchema,
        planId: z.string(),
        trialEnd: z.string().nullable(),
        currentPeriodEnd: z.string().nullable(),
        cancelledAt: z.string().nullable(),
        createdAt: z.string(),
    }).nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const AdminAccountListResponseSchema = createPaginatedResponseSchema(AdminAccountListItemSchema);
// Account names (super admin) - an accountId resolved to something a reader
// recognises, so a dashboard row can name the account it belongs to.
//
// Two bounded reads rather than one directory: the label lookup asks about the
// ids actually on screen, and the picker asks for matches on a search term.
// Neither ever returns "every account" - that is a whole-table read whose cost
// grows with the database, on a surface that only ever displays a page of rows.
export const AccountNameSchema = z.object({
    id: z.string(),
    name: z.string(),
});
export const AccountNamesResponseSchema = z.object({
    accounts: z.array(AccountNameSchema),
});
/**
 * Ids to resolve, as a comma-separated list. Capped because the caller is
 * labelling a rendered page: a request for more ids than a page can hold is a
 * caller reaching for the whole table by another route.
 */
export const ACCOUNT_NAME_IDS_MAX = 100;
export const AccountNamesQuerySchema = z.object({
    ids: z
        .string()
        .transform((value) => value.split(',').map((id) => id.trim()).filter(Boolean))
        .pipe(z.array(z.uuid()).min(1).max(ACCOUNT_NAME_IDS_MAX)),
});
/**
 * The picker's search. `q` has a floor of two characters: a single letter
 * matches most of the table and returns an arbitrary twenty of them, which
 * reads as a broken picker rather than a narrow one.
 */
export const AccountNameSearchQuerySchema = z.object({
    q: z.string().trim().min(2).max(100),
    limit: z.coerce.number().int().min(1).max(50).prefault(20),
});
// Account members overview (super admin) - accepted members plus unaccepted invites.
// An accepted member exposes the linked user; joinedAt is the membership creation date.
export const AdminAccountMemberSchema = z.object({
    userId: z.string(),
    name: z.string(),
    email: z.string(),
    image: z.string().nullable(),
    role: AccountRoleSchema,
    joinedAt: z.string(),
});
// An invite that has not been accepted yet (PENDING, EXPIRED, or CANCELLED).
// ACCEPTED invites are omitted - the person is already listed as a member.
// invitedAt is the original creation date; lastSentAt advances each time the
// invite is resent (so the UI can show that a resend actually took effect).
export const AdminAccountMemberInviteSchema = z.object({
    id: z.string(),
    email: z.string(),
    role: AccountRoleSchema,
    status: AccountInviteStatusSchema,
    invitedAt: z.string(),
    lastSentAt: z.string(),
    expiresAt: z.string(),
});
export const AdminAccountMembersResponseSchema = z.object({
    members: z.array(AdminAccountMemberSchema),
    invites: z.array(AdminAccountMemberInviteSchema),
});
export const AdminAddAccountMemberParamsSchema = z.object({
    accountId: z.string().uuid(),
});
export const AdminAddAccountMemberInputSchema = z.object({
    userId: z.string().uuid(),
    role: AccountRoleSchema,
    idempotencyKey: z.string().min(1),
});
export const AdminAddAccountMemberResponseSchema = z.object({
    id: z.string().uuid(),
    accountId: z.string().uuid(),
    userId: z.string().uuid(),
    role: AccountRoleSchema,
    createdAt: z.string().datetime(),
});
export const AdminUpdateAccountMemberRoleParamsSchema = z.object({
    accountId: z.string().uuid(),
    userId: z.string().uuid(),
});
export const AdminUpdateAccountMemberRoleInputSchema = z.object({
    role: AccountRoleSchema,
});
export const AdminUpdateAccountMemberRoleResponseSchema = z.object({
    accountId: z.string().uuid(),
    userId: z.string().uuid(),
    role: AccountRoleSchema,
    // The role's plain-string form as stored on the membership row before this
    // update, mirroring the `account.member.role_updated` event payload's
    // `oldRole` field (see UpdateMembershipRoleOutcome in
    // account-membership.repository.ts) rather than the narrower AccountRole
    // union - a legacy row is not guaranteed to hold a value in that union.
    previousRole: z.string(),
});
export const AdminRemoveAccountMemberParamsSchema = z.object({
    accountId: z.string().uuid(),
    userId: z.string().uuid(),
});
export const AdminRemoveAccountMemberResponseSchema = z.object({
    success: z.boolean(),
});
// Create an invite for any human role (owner included) as a super admin.
// Bot and staff roles are deliberately excluded. They require a direct add
// through the guarded super-admin provisioning route.
export const AdminCreateAccountInviteInputSchema = z.object({
    email: z.email('Enter a valid email address'),
    role: HumanAccountRoleSchema,
    idempotencyKey: z.string().min(1),
});
// GET /admin/users/lookup?email= - normalised before the lookup, so an
// operator typing mixed case or stray whitespace resolves the same user a
// case-sensitive invite create would.
export const AdminUserLookupQuerySchema = z.object({
    // Normalise BEFORE validating email format: z.email() runs its regex
    // against the raw input, so a chain ordered .email().trim().toLowerCase()
    // would reject a value carrying stray whitespace before the trim ever ran.
    email: z.string().trim().toLowerCase().pipe(z.email()),
});
export const AdminUserLookupResponseSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
});
// =============================================================================
// Account Search
// =============================================================================
export const ClientSearchResultSchema = z.object({
    id: z.string(),
    name: z.string(),
    primaryEmail: z.string().nullable(),
    phone: z.string().nullable(),
    archivedAt: z.string().datetime().nullable(),
    lastActivityDate: z.string().datetime().nullable(),
});
export const MatterSearchResultSchema = z.object({
    id: z.string(),
    name: z.string(),
    displayReference: z.string(),
    status: ClientProjectStatusSchema,
    archivedAt: z.string().datetime().nullable(),
    client: z.object({ id: z.string(), name: z.string() }),
});
export const InvoiceSearchResultSchema = z.object({
    id: z.string(),
    invoiceNumber: z.string().nullable(),
    reference: z.string().nullable(),
    status: InvoiceStatusSchema,
    date: z.string().datetime(),
    // Optional so a client built before it existed still parses.
    dueDate: z.string().datetime().nullable().optional(),
    totalMinorUnits: z.number().int(),
    currency: z.string(),
    client: z.object({ id: z.string(), name: z.string() }),
    // The facts the invoice status badge reads, resolved as the invoice list
    // resolves them. Optional so a client built before they existed still parses.
    paymentStatus: InvoicePaymentStatusSchema.optional(),
    settledAmountMinorUnits: z.number().int().optional(),
    creditedAmountMinorUnits: z.number().int().optional(),
    writtenOffAmountMinorUnits: z.number().int().optional(),
    liveProforma: LiveProformaSchema.omit({ matchesInvoice: true }).nullable().optional(),
});
export const AccountSearchQuerySchema = z.object({
    q: z.string().trim().min(1).max(200),
    include: z.array(z.enum(['clients', 'matters', 'invoices'])).default(['clients', 'matters']),
    limit: z.coerce.number().int().min(1).max(100).prefault(25),
});
export const AccountSearchResponseSchema = z.object({
    clients: z.array(ClientSearchResultSchema).optional(),
    matters: z.array(MatterSearchResultSchema).optional(),
    invoices: z.array(InvoiceSearchResultSchema).optional(),
    totalCount: z.object({
        clients: z.number().int().optional(),
        matters: z.number().int().optional(),
        invoices: z.number().int().optional(),
    }),
});
// =============================================================================
// Fee earner rates
//
// A fee earner's own hourly rate on one client or one matter. At most one per
// fee earner per client and per matter, so the lists are bounded by the account team.
// =============================================================================
export const ClientFeeEarnerRatesParamsSchema = z.object({
    accountId: z.string(),
    clientId: LowercaseUuidSchema,
});
export const ClientFeeEarnerRateParamsSchema = ClientFeeEarnerRatesParamsSchema.extend({
    membershipId: LowercaseUuidSchema,
});
export const ClientProjectFeeEarnerRatesParamsSchema = z.object({
    accountId: z.string(),
    clientProjectId: LowercaseUuidSchema,
});
export const ClientProjectFeeEarnerRateParamsSchema = ClientProjectFeeEarnerRatesParamsSchema.extend({
    membershipId: LowercaseUuidSchema,
});
export const FeeEarnerRateSchema = z.object({
    membershipId: z.string(),
    hourlyRateMinorUnits: z.int(),
    updatedAt: z.string(),
});
export const ListFeeEarnerRatesResponseSchema = z.object({
    data: z.array(FeeEarnerRateSchema),
});
/**
 * The minimum is 1, not 0: the rate resolver reads 0 as "not set", so a stored
 * 0 would silently fall through to the next rate. Clear a rate with DELETE.
 */
export const SetFeeEarnerRateInputSchema = z.strictObject({
    hourlyRateMinorUnits: z
        .int()
        .min(1, 'Rate must be greater than 0')
        .max(MAX_MINOR_UNITS)
        .describe('Hourly rate in minor units (cents): rand x 100, so R2,500.00 an hour is 250000.'),
});
export const ClearFeeEarnerRateResponseSchema = z.object({ success: z.boolean() });
